import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { physioAPI } from '../../utils/api';

const EXERCISES = [
    'Hot/Cold Pack', 'TENS', 'Ultrasound Therapy', 'Shortwave Diathermy',
    'Passive ROM', 'Active ROM', 'Stretching', 'Strengthening Exercises',
    'Gait Training', 'Balance Training', 'Breathing Exercises', 'Manual Therapy',
    'Taping / Strapping', 'Hydrotherapy', 'Postural Correction', 'Core Stabilization'
];

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

function PainScale({ value, onChange }) {
    return (
        <div style={{ display: 'flex', gap: 4 }}>
            {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                <button key={n} onClick={() => onChange(n)}
                    style={{ width: 30, height: 30, borderRadius: 6, border: `2px solid ${value === n ? '#0369a1' : '#e2e8f0'}`,
                        background: value === n ? '#0369a1' : (n >= 7 ? '#fee2e2' : n >= 4 ? '#fef3c7' : '#f0fdf4'),
                        color: value === n ? 'white' : '#374151', cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: 0 }}>
                    {n}
                </button>
            ))}
        </div>
    );
}

export default function PhysioDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [referral, setReferral] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSessionForm, setShowSessionForm] = useState(false);
    const [sessionForm, setSessionForm] = useState({
        session_date: new Date().toISOString().split('T')[0],
        exercises: [],
        pain_score_before: null,
        pain_score_after: null,
        progress_notes: '',
        duration_minutes: 30
    });
    const [saving, setSaving] = useState(false);
    const [completing, setCompleting] = useState(false);

    const load = async () => {
        try {
            const res = await physioAPI.getReferral(id);
            setReferral(res.data.referral);
            setSessions(res.data.sessions);
        } catch { toast.error('Failed to load'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    const setF = (k, v) => setSessionForm(f => ({ ...f, [k]: v }));

    const toggleExercise = (ex) => {
        setSessionForm(f => ({
            ...f,
            exercises: f.exercises.includes(ex) ? f.exercises.filter(e => e !== ex) : [...f.exercises, ex]
        }));
    };

    const handleAddSession = async () => {
        if (sessionForm.exercises.length === 0) return toast.error('Select at least one exercise/treatment');
        setSaving(true);
        try {
            await physioAPI.addSession({
                referral_id: id,
                patient_id: referral.patient_id,
                ...sessionForm,
                exercises: sessionForm.exercises.join(', ')
            });
            toast.success('Session recorded');
            setShowSessionForm(false);
            setSessionForm({ session_date: new Date().toISOString().split('T')[0], exercises: [], pain_score_before: null, pain_score_after: null, progress_notes: '', duration_minutes: 30 });
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
        } finally { setSaving(false); }
    };

    const handleComplete = async () => {
        if (!window.confirm('Mark this referral as Completed?')) return;
        setCompleting(true);
        try {
            await physioAPI.updateReferral(id, { status: 'Completed' });
            toast.success('Referral completed');
            navigate('/physio');
        } catch { toast.error('Failed'); }
        finally { setCompleting(false); }
    };

    const handleDelete = async (sessionId) => {
        if (!window.confirm('Delete this session?')) return;
        try {
            await physioAPI.deleteSession(sessionId);
            toast.success('Deleted');
            load();
        } catch { toast.error('Failed'); }
    };

    const printCard = () => {
        if (!referral) return;
        const win = window.open('', '_blank', 'width=800,height=600');
        win.document.write(`<!DOCTYPE html><html><head><title>Physio Card</title>
        <style>
            body { font-family: Arial, sans-serif; font-size: 13px; margin: 20px; color: #1e293b; }
            h2 { font-size: 18px; border-bottom: 2px solid #0369a1; padding-bottom: 6px; color: #0369a1; }
            table { width: 100%; border-collapse: collapse; margin: 12px 0; }
            th, td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; }
            th { background: #eff6ff; font-weight: 700; font-size: 12px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 10px 0; }
            .info-item { background: #f8fafc; padding: 8px; border-radius: 4px; }
            .label { font-size: 10px; color: #64748b; font-weight: 600; text-transform: uppercase; }
            @media print { button { display: none; } }
        </style></head><body>
        <h2>Physiotherapy Treatment Card</h2>
        <div class="info-grid">
            <div class="info-item"><div class="label">Patient</div><strong>${referral.full_name}</strong></div>
            <div class="info-item"><div class="label">MRN</div>${referral.mrn}</div>
            <div class="info-item"><div class="label">Diagnosis</div>${referral.diagnosis || '—'}</div>
            <div class="info-item"><div class="label">Goal</div>${referral.treatment_goals || '—'}</div>
            <div class="info-item"><div class="label">Sessions Prescribed</div>${referral.sessions_prescribed}</div>
            <div class="info-item"><div class="label">Sessions Done</div>${sessions.length}</div>
        </div>
        <table>
            <thead><tr><th>#</th><th>Date</th><th>Treatment</th><th>Pain Before</th><th>Pain After</th><th>Duration</th><th>Notes</th></tr></thead>
            <tbody>
                ${sessions.map(s => `<tr>
                    <td>${s.session_number}</td>
                    <td>${new Date(s.session_date).toLocaleDateString()}</td>
                    <td>${s.exercises || '—'}</td>
                    <td>${s.pain_score_before ?? '—'}</td>
                    <td>${s.pain_score_after ?? '—'}</td>
                    <td>${s.duration_minutes ? s.duration_minutes + ' min' : '—'}</td>
                    <td>${s.progress_notes || '—'}</td>
                </tr>`).join('')}
            </tbody>
        </table>
        <button onclick="window.print()">Print</button>
        </body></html>`);
        win.document.close();
        win.focus();
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>;
    if (!referral) return <div style={{ textAlign: 'center', padding: 60, color: '#dc2626' }}>Referral not found</div>;

    const pct = referral.sessions_prescribed ? Math.min(100, Math.round((sessions.length / referral.sessions_prescribed) * 100)) : 0;

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={() => navigate('/physio')} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', padding: '6px 12px', fontSize: 13 }}>← Back</button>
                    <div>
                        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0369a1', margin: 0 }}>{referral.full_name}</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{referral.mrn} · {referral.gender} · Age {referral.age_years}</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={printCard} style={{ padding: '8px 16px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>🖨️ Print Card</button>
                    <button onClick={() => setShowSessionForm(!showSessionForm)} style={{ padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>+ Add Session</button>
                    {referral.status === 'Active' && (
                        <button onClick={handleComplete} disabled={completing} style={{ padding: '8px 16px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                            {completing ? '...' : '✓ Complete'}
                        </button>
                    )}
                </div>
            </div>

            {/* Info Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                    { label: 'Diagnosis', value: referral.diagnosis || '—' },
                    { label: 'Goal', value: referral.treatment_goals || '—' },
                    { label: 'Referred From', value: referral.source },
                    { label: 'Status', value: referral.status },
                ].map(item => (
                    <div key={item.label} style={{ background: 'white', borderRadius: 8, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>{item.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.value}</div>
                    </div>
                ))}
            </div>

            {/* Progress bar */}
            <div style={{ background: 'white', borderRadius: 10, padding: '14px 18px', marginBottom: 20, border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Session Progress</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0369a1' }}>{sessions.length} / {referral.sessions_prescribed} sessions ({pct}%)</span>
                </div>
                <div style={{ height: 10, background: '#e2e8f0', borderRadius: 5 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: pct >= 80 ? '#16a34a' : pct >= 50 ? '#d97706' : '#0369a1', borderRadius: 5, transition: 'width 0.4s' }} />
                </div>
                {referral.notes && <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>Notes: {referral.notes}</div>}
            </div>

            {/* Session Form */}
            {showSessionForm && (
                <div style={{ background: 'white', borderRadius: 10, padding: 20, marginBottom: 20, border: '1px solid #bbf7d0' }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e3a5f', marginBottom: 16 }}>Record Session #{sessions.length + 1}</h3>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Date</label>
                            <input type="date" style={inputStyle} value={sessionForm.session_date} onChange={e => setF('session_date', e.target.value)} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Duration (minutes)</label>
                            <input type="number" min="5" max="180" style={inputStyle} value={sessionForm.duration_minutes} onChange={e => setF('duration_minutes', parseInt(e.target.value))} />
                        </div>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 8 }}>Treatment / Exercises *</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {EXERCISES.map(ex => (
                                <button key={ex} onClick={() => toggleExercise(ex)}
                                    style={{ padding: '5px 12px', borderRadius: 20, border: `1px solid ${sessionForm.exercises.includes(ex) ? '#0369a1' : '#e2e8f0'}`,
                                        background: sessionForm.exercises.includes(ex) ? '#0369a1' : 'white',
                                        color: sessionForm.exercises.includes(ex) ? 'white' : '#374151',
                                        cursor: 'pointer', fontSize: 12 }}>
                                    {ex}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 8 }}>Pain Score Before (0-10)</label>
                            <PainScale value={sessionForm.pain_score_before} onChange={v => setF('pain_score_before', v)} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 8 }}>Pain Score After (0-10)</label>
                            <PainScale value={sessionForm.pain_score_after} onChange={v => setF('pain_score_after', v)} />
                        </div>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Progress Notes</label>
                        <textarea style={{ ...inputStyle, height: 80, resize: 'vertical' }} value={sessionForm.progress_notes}
                            onChange={e => setF('progress_notes', e.target.value)}
                            placeholder="Patient response, improvement, concerns..." />
                    </div>

                    <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={handleAddSession} disabled={saving}
                            style={{ padding: '9px 24px', background: saving ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            {saving ? 'Saving...' : 'Save Session'}
                        </button>
                        <button onClick={() => setShowSessionForm(false)} style={{ padding: '9px 16px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                    </div>
                </div>
            )}

            {/* Sessions List */}
            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>
                    Session History ({sessions.length})
                </div>
                {sessions.length === 0 ? (
                    <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No sessions recorded yet</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                                <th style={{ padding: '10px 14px', textAlign: 'left' }}>#</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left' }}>Date</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left' }}>Treatment</th>
                                <th style={{ padding: '10px 14px', textAlign: 'center' }}>Pain Before</th>
                                <th style={{ padding: '10px 14px', textAlign: 'center' }}>Pain After</th>
                                <th style={{ padding: '10px 14px', textAlign: 'center' }}>Duration</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left' }}>Notes</th>
                                <th style={{ padding: '10px 14px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.map((s, i) => (
                                <tr key={s.id} style={{ borderTop: '1px solid #f1f5f9', fontSize: 13 }}>
                                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#0369a1' }}>{s.session_number}</td>
                                    <td style={{ padding: '10px 14px' }}>{new Date(s.session_date).toLocaleDateString('en-PK')}</td>
                                    <td style={{ padding: '10px 14px', maxWidth: 200 }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                            {(s.exercises || '').split(', ').filter(Boolean).map(ex => (
                                                <span key={ex} style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 10, padding: '2px 8px', fontSize: 11 }}>{ex}</span>
                                            ))}
                                        </div>
                                    </td>
                                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                        {s.pain_score_before != null ? (
                                            <span style={{ fontWeight: 700, color: s.pain_score_before >= 7 ? '#dc2626' : s.pain_score_before >= 4 ? '#d97706' : '#16a34a' }}>{s.pain_score_before}/10</span>
                                        ) : '—'}
                                    </td>
                                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                        {s.pain_score_after != null ? (
                                            <span style={{ fontWeight: 700, color: s.pain_score_after >= 7 ? '#dc2626' : s.pain_score_after >= 4 ? '#d97706' : '#16a34a' }}>{s.pain_score_after}/10</span>
                                        ) : '—'}
                                    </td>
                                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>{s.duration_minutes ? `${s.duration_minutes}m` : '—'}</td>
                                    <td style={{ padding: '10px 14px', color: '#475569', fontSize: 12 }}>{s.progress_notes || '—'}</td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <button onClick={() => handleDelete(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16 }}>🗑</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
